/**
 * cart-drawer.js — Motorsports Decal Brand
 * Shopify AJAX Cart Drawer
 * Compatible with legacy/non-Dawn themes.
 * No external dependencies.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Helpers
  ───────────────────────────────────────────── */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escAttr(str) { return esc(str); }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || null;
      return fmt ? Shopify.formatMoney(cents, fmt) : Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ─────────────────────────────────────────────
     CartDrawer Class
  ───────────────────────────────────────────── */
  class CartDrawer {
    constructor() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;

      this.body         = this.drawer.querySelector('[data-cart-drawer-body]');
      this.footer       = this.drawer.querySelector('[data-cart-drawer-footer]');
      this.subtotal     = this.drawer.querySelector('[data-cart-drawer-subtotal]');
      this.total        = this.drawer.querySelector('[data-cart-drawer-total]');
      this.discountRow  = this.drawer.querySelector('[data-cart-drawer-discount-row]');
      this.discountVal  = this.drawer.querySelector('[data-cart-drawer-discount]');

      this._isOpen    = false;
      this._lastFocus = null;

      this._bindEvents();
    }

    /* ── Event wiring ── */
    _bindEvents() {
      /* Close buttons & overlay */
      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-drawer-close]')) this.close();
      });

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._isOpen) this.close();
      });

      /* Custom events from product page / theme */
      document.addEventListener('cart:drawer:open', () => {
        this.refresh().then(() => this.open());
      });

      document.addEventListener('cart:updated', () => {
        if (this._isOpen) this.refresh();
        this._updateCountBadges();
      });

      /* Delegated clicks — qty & remove */
      document.addEventListener('click', (e) => {
        /* Open trigger (cart icon etc.) */
        if (e.target.closest('[data-open-cart-drawer]')) {
          e.preventDefault();
          this.refresh().then(() => this.open());
          return;
        }

        /* Qty buttons */
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn) {
          e.preventDefault();
          this._changeLine(
            Number(qtyBtn.dataset.line),
            Number(qtyBtn.dataset.quantity)
          );
          return;
        }

        /* Remove buttons */
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this._changeLine(Number(removeBtn.dataset.line), 0);
          return;
        }
      });
    }

    /* ── Open / Close ── */
    open() {
      if (this._isOpen) return;
      this._isOpen    = true;
      this._lastFocus = document.activeElement;

      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      document.body.classList.add('cart-drawer-open');

      /* Focus panel for keyboard users */
      const panel = this.drawer.querySelector('.cd__panel');
      if (panel) requestAnimationFrame(() => panel.focus());
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;

      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      document.body.classList.remove('cart-drawer-open');

      /* Restore focus */
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
      }
    }

    /* ── Fetch & refresh ── */
    async refresh() {
      this._setLoading();
      try {
        const res  = await fetch('/cart.js', { cache: 'no-store' });
        const cart = await res.json();
        if (!res.ok) throw cart;
        this._render(cart);
        this._updateCountBadges(cart.item_count);
      } catch (err) {
        console.error('[CartDrawer] refresh failed:', err);
        this._renderError();
      }
    }

    async _changeLine(line, quantity) {
      this._setLoading();
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        const cart = await res.json();
        if (!res.ok) throw cart;
        this._render(cart);
        this._updateCountBadges(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (err) {
        console.error('[CartDrawer] changeLine failed:', err);
        this.refresh(); /* Fallback: re-fetch clean state */
      }
    }

    /* ── Render full cart ── */
    _render(cart) {
      if (!cart || !this.body) return;

      if (!cart.items || cart.items.length === 0) {
        this.body.innerHTML = this._tmplEmpty();
      } else {
        this.body.innerHTML = cart.items
          .map((item, i) => this._tmplItem(item, i + 1))
          .join('');
      }

      /* Summary */
      const discount = Math.max(
        (cart.original_total_price || 0) - (cart.total_price || 0),
        0
      );

      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(
          cart.original_total_price || cart.total_price || 0
        );
      }
      if (this.total) {
        this.total.textContent = formatMoney(cart.total_price || 0);
      }
      if (this.discountRow && this.discountVal) {
        if (discount > 0) {
          this.discountRow.hidden = false;
          this.discountVal.textContent = '−' + formatMoney(discount);
        } else {
          this.discountRow.hidden = true;
          this.discountVal.textContent = '';
        }
      }
    }

    /* ── Templates ── */
    _tmplItem(item, line) {
      const imgTag = item.image
        ? `<img src="${escAttr(item.image)}" alt="${esc(item.product_title)}" loading="lazy" width="88" height="88">`
        : '';

      const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<div class="cd__variant">${esc(item.variant_title)}</div>`
        : '';

      const isDiscounted = item.original_line_price > item.final_line_price;
      const badgeHtml    = isDiscounted
        ? `<div class="cd__badge">⚡ Discount Applied</div>`
        : '';
      const compareHtml  = isDiscounted
        ? `<span class="cd__compare-price">${formatMoney(item.original_line_price)}</span>`
        : '';

      const propsHtml = this._tmplProps(item.properties);

      const qtyMinus = Math.max(item.quantity - 1, 0);
      const qtyPlus  = item.quantity + 1;

      return `
<div class="cd__item" data-line="${line}">
  <a href="${escAttr(item.url)}" class="cd__img-wrap" tabindex="-1" aria-hidden="true">
    ${imgTag}
  </a>

  <div class="cd__content">
    <a href="${escAttr(item.url)}" class="cd__name" title="${esc(item.product_title)}">
      ${esc(item.product_title)}
    </a>

    ${variantHtml}
    ${badgeHtml}

    <div class="cd__prices">
      <span class="cd__price">${formatMoney(item.final_line_price)}</span>
      ${compareHtml}
    </div>

    ${propsHtml}

    <div class="cd__item-actions">
      <div class="cd__qty" role="group" aria-label="Quantity for ${esc(item.product_title)}">
        <button
          type="button"
          class="cd__qty-btn"
          data-cart-qty
          data-line="${line}"
          data-quantity="${qtyMinus}"
          aria-label="Decrease quantity"
        >−</button>
        <span class="cd__qty-num" aria-live="polite">${item.quantity}</span>
        <button
          type="button"
          class="cd__qty-btn"
          data-cart-qty
          data-line="${line}"
          data-quantity="${qtyPlus}"
          aria-label="Increase quantity"
        >+</button>
      </div>

      <button
        type="button"
        class="cd__remove"
        data-cart-remove
        data-line="${line}"
        aria-label="Remove ${esc(item.product_title)}"
      >Remove</button>
    </div>
  </div>
</div>`;
    }

    _tmplProps(props) {
      if (!props) return '';
      const entries = Object.entries(props).filter(
        ([k, v]) => k && v && k.charAt(0) !== '_'
      );
      if (!entries.length) return '';

      const rows = entries.map(([k, v]) => {
        const isUrl = typeof v === 'string' && /^https?:\/\//i.test(v);
        const valHtml = isUrl
          ? `<a href="${escAttr(v)}" target="_blank" rel="noopener noreferrer">${esc(v)}</a>`
          : esc(String(v));
        return `<div class="cd__prop">
          <span class="cd__prop-key">${esc(k)}:</span>
          <span class="cd__prop-val">${valHtml}</span>
        </div>`;
      }).join('');

      return `<div class="cd__props">${rows}</div>`;
    }

    _tmplEmpty() {
      return `
<div class="cd__empty">
  <div class="cd__empty-icon" aria-hidden="true">
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      <path d="M4 4h3l2.5 12h11l2-8H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="square"/>
      <circle cx="12" cy="22" r="1.5" fill="currentColor"/>
      <circle cx="19" cy="22" r="1.5" fill="currentColor"/>
    </svg>
  </div>
  <h3>Cart is empty</h3>
  <p>Add custom graphics, decals, or accessories to get rolling.</p>
  <a href="/collections/all" class="cd__btn cd__btn--primary">Shop Now</a>
</div>`;
    }

    /* ── State helpers ── */
    _setLoading() {
      if (!this.body) return;
      this.body.innerHTML = `
<div class="cd__loading">
  <span class="cd__spinner" aria-hidden="true"></span>
  <span class="cd__loading-text">Loading your cart</span>
</div>`;
    }

    _renderError() {
      if (!this.body) return;
      this.body.innerHTML = `
<div class="cd__empty">
  <h3>Something went wrong</h3>
  <p>Unable to load your cart. Please try again.</p>
  <a href="/cart" class="cd__btn cd__btn--primary">Go to Cart</a>
</div>`;
    }

    /* ── Update cart count badges across theme ── */
    _updateCountBadges(count) {
      if (count === undefined) return;
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.setAttribute('aria-label', `${count} items in cart`);
        el.hidden = count === 0;
      });
    }
  }

  /* ─────────────────────────────────────────────
     AddToCart — Loading / Success state handler
  ───────────────────────────────────────────── */
  class AddToCartManager {
    constructor() {
      this._bindEvents();
    }

    _bindEvents() {
      /* Listen for ATC form submits */
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;

        const btn = form.querySelector('[type="submit"]');
        if (!btn) return;

        /* Prevent double-submit */
        if (btn.classList.contains('btn-atc--loading')) {
          e.preventDefault();
          return;
        }

        /* Intercept and use fetch */
        e.preventDefault();
        this._handleSubmit(form, btn);
      });

      /* Also support [data-atc-form] attribute for non-standard forms */
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-atc-btn]');
        if (!btn) return;
        const formId = btn.dataset.atcBtn;
        const form   = formId ? document.getElementById(formId) : btn.closest('form');
        if (!form) return;
        e.preventDefault();
        if (!btn.classList.contains('btn-atc--loading')) {
          this._handleSubmit(form, btn);
        }
      });
    }

    async _handleSubmit(form, btn) {
      this._setLoading(btn);

      try {
        const formData = new FormData(form);
        const res      = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();

        if (!res.ok) {
          const msg = data.description || data.message || 'Error adding item.';
          this._setError(btn, msg);
          return;
        }

        this._setSuccess(btn);

        /* Fire events for CartDrawer and any other listeners */
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        document.dispatchEvent(new CustomEvent('cart:drawer:open'));
      } catch (err) {
        console.error('[AddToCart] failed:', err);
        this._setError(btn, 'Network error. Please try again.');
      }
    }

    _setLoading(btn) {
      btn.classList.add('btn-atc', 'btn-atc--loading');
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');

      /* Ensure spinner element exists */
      if (!btn.querySelector('.btn-atc__text')) {
        btn.innerHTML = `
          <span class="btn-atc__text">${btn.innerHTML}</span>
          <span class="btn-atc__spinner" aria-hidden="true"></span>`;
      }
    }

    _setSuccess(btn) {
      btn.classList.remove('btn-atc--loading', 'btn-atc--error');
      btn.classList.add('btn-atc--success');
      btn.setAttribute('aria-busy', 'false');

      const textEl = btn.querySelector('.btn-atc__text');
      if (textEl) textEl.textContent = 'Added!';

      setTimeout(() => this._reset(btn, 'Add to Cart'), 1800);
    }

    _setError(btn, message) {
      btn.classList.remove('btn-atc--loading');
      btn.classList.add('btn-atc--error');
      btn.setAttribute('aria-busy', 'false');
      btn.setAttribute('aria-disabled', 'false');

      const textEl = btn.querySelector('.btn-atc__text');
      if (textEl) textEl.textContent = message;

      setTimeout(() => this._reset(btn), 3000);
    }

    _reset(btn, label) {
      btn.classList.remove(
        'btn-atc--loading',
        'btn-atc--success',
        'btn-atc--error'
      );
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-label');

      /* Restore original text if we wrapped it */
      const textEl = btn.querySelector('.btn-atc__text');
      if (textEl) {
        if (label) textEl.textContent = label;
        /* Unwrap on next interaction to keep it clean */
      }
    }
  }

  /* ─────────────────────────────────────────────
     Init
  ───────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    new CartDrawer();
    new AddToCartManager();
  });
})();
